import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { supabase, TowJob, Profile } from '../lib/supabase'
import { hasRole } from '../lib/roleUtils'
import {
  ArrowLeft,
  Loader2,
  CreditCard,
  Receipt,
  Truck,
  CheckCircle2,
  Clock,
  Download,
  Filter,
  Search,
  Activity,
  TrendingUp
} from 'lucide-react'
import { jsPDF } from 'jspdf'

interface PaymentRecord {
  id: string
  job_id: string
  job_reference: string
  tower_id: string
  tower_name: string
  garage_id: string
  garage_name: string
  amount: number
  payment_reference: string
  document_number: string
  payment_method: string
  paid_at: string
  status: string
}

export default function PaymentTrackingPage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [payments, setPayments] = useState<PaymentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadPayments()
  }, [profile])

  const loadPayments = async () => {
    if (!profile) return

    setLoading(true)

    try {
      // Load all completed jobs with payment notes
      const { data: jobs } = await supabase
        .from('tow_jobs')
        .select('*')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })

      if (!jobs) {
        setPayments([])
        setLoading(false)
        return
      }

      // Get tower and garage profiles
      const towerIds = jobs.map(j => j.selected_tower_id || j.operator_id).filter(Boolean)
      const garageIds = jobs.map(j => j.destination_garage_id).filter(Boolean)

      const [towers, garages] = await Promise.all([
        towerIds.length > 0 ? supabase.from('profiles').select('*').in('id', towerIds) : Promise.resolve({ data: [] }),
        garageIds.length > 0 ? supabase.from('profiles').select('*').in('id', garageIds) : Promise.resolve({ data: [] })
      ])

      const towerMap = new Map(towers.data?.map(t => [t.id, t.full_name || 'Unknown']) || [])
      const garageMap = new Map(garages.data?.map(g => [g.id, g.business_name || 'Unknown']) || [])

      // Parse payment records from job notes
      const paymentRecords: PaymentRecord[] = []
      for (const job of jobs) {
        // Check for both old format "PAID " and new format "PAID|"
        if (job.diagnosis_notes?.includes('PAID')) {
          let paymentRef = 'N/A'
          let docNumber = 'N/A'
          let paidDate = job.completed_at || job.created_at

          // Try to parse new format: PAID|PAY-xxxxxxx|DOC-xxxxxxx|timestamp|notes
          if (job.diagnosis_notes.includes('PAID|')) {
            const parts = job.diagnosis_notes.split('|')
            if (parts.length >= 4) {
              paymentRef = parts[1] || 'N/A'
              docNumber = parts[2] || 'N/A'
              paidDate = parts[3] || job.completed_at
            }
          } else {
            // Old format: PAID timestamp - notes
            const match = job.diagnosis_notes.match(/PAID\s+(\S+)/)
            if (match) {
              paidDate = match[1]
            }
          }

          paymentRecords.push({
            id: job.id,
            job_id: job.id,
            job_reference: job.reference,
            tower_id: job.selected_tower_id || job.operator_id || '',
            tower_name: towerMap.get(job.selected_tower_id || job.operator_id || '') || 'Unknown',
            garage_id: job.destination_garage_id || '',
            garage_name: garageMap.get(job.destination_garage_id || '') || 'Unknown',
            amount: job.total_amount,
            payment_reference: paymentRef,
            document_number: docNumber,
            payment_method: 'cash',
            paid_at: paidDate,
            status: 'completed'
          })
        }
      }

      console.log('Payment records found:', paymentRecords.length)

      // Filter by role - users only see their own payments
      let filteredRecords = paymentRecords
      if (hasRole(profile, 'garage')) {
        // Garage only sees their own payments
        filteredRecords = paymentRecords.filter(p => p.garage_id === profile.id)
      } else if (hasRole(profile, 'tower')) {
        // Tower only sees their own payments (check both selected_tower_id and operator_id)
        filteredRecords = paymentRecords.filter(p => p.tower_id === profile.id)
      } else if (hasRole(profile, 'parts_vendor')) {
        // Parts vendors don't have payment records in tow_jobs, so show empty
        filteredRecords = []
      } else if (profile.role === 'customer') {
        // Customers only see their own payments
        filteredRecords = paymentRecords.filter(p => p.job.customer_id === profile.id)
      }
      // Admin sees all payments

      console.log('Filtered payment records for role', profile.role, ':', filteredRecords.length)
      console.log('Profile ID:', profile.id)
      setPayments(filteredRecords)
    } catch (error) {
      console.error('Error loading payments:', error)
    }

    setLoading(false)
  }

  const filteredPayments = payments.filter(p => {
    const matchesSearch = !searchTerm ||
      p.job_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.payment_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.tower_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.garage_name.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  const downloadReceipt = (payment: PaymentRecord) => {
    const doc = new jsPDF()
    
    doc.setFontSize(16)
    doc.setTextColor(192, 57, 43)
    doc.text('Mkononi Payment Receipt', 105, 20, { align: 'center' })
    
    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 30, { align: 'center' })
    
    doc.setFontSize(12)
    doc.setTextColor(0)
    doc.text('Payment Details', 20, 50)
    
    doc.setFontSize(10)
    doc.text(`Reference: ${payment.job_reference}`, 20, 60)
    doc.text(`Payment Reference: ${payment.payment_reference}`, 20, 70)
    doc.text(`Document Number: ${payment.document_number}`, 20, 80)
    doc.text(`Amount: KES ${Number(payment.amount).toLocaleString()}`, 20, 90)
    doc.text(`Date: ${new Date(payment.paid_at).toLocaleDateString('en-KE')}`, 20, 100)
    doc.text(`Status: ${payment.status}`, 20, 110)
    
    doc.text('', 20, 120)
    doc.setFontSize(12)
    doc.text('Tower Information', 20, 130)
    doc.setFontSize(10)
    doc.text(`Name: ${payment.tower_name}`, 20, 140)
    doc.text(`ID: ${payment.tower_id}`, 20, 150)
    
    doc.text('', 20, 160)
    doc.setFontSize(12)
    doc.text('Garage Information', 20, 170)
    doc.setFontSize(10)
    doc.text(`Name: ${payment.garage_name}`, 20, 180)
    doc.text(`ID: ${payment.garage_id}`, 20, 190)
    
    doc.save(`receipt-${payment.job_reference}.pdf`)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1rem',
            background: '#fef2f2',
            border: '1px solid #dc2626',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            color: '#dc2626',
            fontWeight: 600,
            fontSize: '0.875rem'
          }}
        >
          <ArrowLeft size={16} style={{ marginRight: '0.5rem' }} />
          Back
        </button>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
            Payment Tracking
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            {hasRole(profile, 'admin') ? 'All platform payments' :
             hasRole(profile, 'garage') ? 'Your garage payments' :
             hasRole(profile, 'tower') ? 'Your payment records' : 'Payment records'}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1.5rem' }}>
        {/* Compact Stats Bar */}
        <div className="payment-stats-bar payment-filters" style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem 1.5rem',
          background: 'white',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '150px' }}>
            <Receipt size={20} style={{ color: 'var(--red)' }} />
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>Total Payments</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>{payments.length}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '150px' }}>
            <CreditCard size={20} style={{ color: 'var(--green)' }} />
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>Total Amount</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--green)' }}>KES {payments.reduce((sum, p) => sum + Number(p.amount), 0).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
            <input
              type="text"
              placeholder="Search by reference, payment ref, document #, tower, or garage..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="form-input"
              style={{ paddingLeft: '2.5rem', width: '100%' }}
            />
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              <Loader2 size={48} className="spin" style={{ color: 'var(--red)', marginBottom: '1rem' }} />
              <div style={{ fontSize: '0.9rem' }}>Loading payment records...</div>
            </div>
          </div>
        ) : (
          <>
            {/* Empty State */}
            {filteredPayments.length === 0 ? (
              <div className="card">
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Receipt size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                    No Payment Records Found
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                    {searchTerm ? 'Try a different search term' : 'No payments have been recorded yet'}
                  </div>
                </div>
              </div>
            ) : (
              /* Payment Records Table */
              <div className="card" style={{ overflow: 'hidden' }}>
                <table className="data-table payment-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Tower</th>
                      <th>Garage</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Payment Ref</th>
                      <th>Document #</th>
                      <th>Date</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map(payment => (
                      <tr key={payment.id}>
                        <td style={{ fontWeight: 600 }}>{payment.job_reference}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{payment.tower_name}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{payment.garage_name}</div>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>
                          KES {Number(payment.amount).toLocaleString()}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {payment.payment_reference}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {payment.document_number}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {new Date(payment.paid_at).toLocaleDateString('en-KE')}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-green" style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Paid</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => downloadReceipt(payment)}
                            className="btn btn-sm"
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.75rem',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            <Download size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile card version */}
                <div className="data-table-card-wrapper payment-table-card" style={{ display: 'none' }}>
                  {filteredPayments.map(payment => (
                    <div key={payment.id} className="data-table-card">
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Reference</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600 }}>{payment.job_reference}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Tower</span>
                        <span className="data-table-card-value">{payment.tower_name}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Garage</span>
                        <span className="data-table-card-value">{payment.garage_name}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Amount</span>
                        <span className="data-table-card-value" style={{ fontWeight: 600, color: 'var(--green)' }}>
                          KES {Number(payment.amount).toLocaleString()}
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Payment Ref</span>
                        <span className="data-table-card-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {payment.payment_reference}
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Document #</span>
                        <span className="data-table-card-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {payment.document_number}
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Date</span>
                        <span className="data-table-card-value">{new Date(payment.paid_at).toLocaleDateString('en-KE')}</span>
                      </div>
                      <div className="data-table-card-row">
                        <span className="data-table-card-label">Status</span>
                        <span className="data-table-card-value">
                          <span className="badge badge-green" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>Paid</span>
                        </span>
                      </div>
                      <div className="data-table-card-row">
                        <button
                          onClick={() => downloadReceipt(payment)}
                          className="btn btn-sm"
                          style={{
                            width: '100%',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <Download size={12} style={{ marginRight: '0.5rem' }} />
                          Download Receipt
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
